import React, { useState, useRef, useEffect, useMemo } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal.js";
import AlertNotification from "../../components/AlertNotification";
import LoadingNotification from "../../components/LoadingNotification";
import StockTags from "../../components/StockTags";
import Pagination from "../../components/Pagination";
import apiHelper from "../../apiHelper.js";
import noImage from "../../assets/img/brand/noImage.png";
import emptyBoxImg from "../../assets/img/brand/no-brand-img.png";
import { calculatePlatformPrice, normalizeFees, normalizeMarkups } from "../../utils/pricingTemplate.js";
import {
  isWooDrivenBasePrice,
  wooBasePriceDriver,
  isBasePriceEmpty,
  WOO_BASE_PRICE_LOCKED_TITLE,
  BASE_PRICE_BREATHE_TITLE,
} from "../../utils/basePriceSource";

// Rows per page, matching Platform Pricing's own table so the two screens
// paginate the same way. This paginates the RENDER only: `selectedItems`,
// `editedPrices` and the Save payload all stay whole, so the bulk fill still
// applies to every selected listing and Save still submits every one of them.
// Rendering all of them at once is what made a large selection unusable.
const BP_PAGE_SIZE = 20;

const BULK_TYPES = ["Bulk Input", "Use Base Price", "Use Pricing Template"];

const BULK_TYPE_DISPLAY = {
  "Bulk Input": "Bulk Input",
  "Use Base Price": "Use Base Price",
  "Use Pricing Template": "Use Pricing Template",
};

function formatSGD(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(String(value).replace(/[^\d.-]/g, ""));
  if (isNaN(num)) return "";
  return num.toFixed(2);
}

const isLazadaPlatform = (name = "") => name.includes("Lazada");

// Comma-grouped "1,500.00", matching the Platform Listing / Platform Pricing screens.
function formatMoney(value) {
  const num = Number(value);
  if (isNaN(num)) return "-";
  return num.toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Only allow digits with an optional decimal point, matching the Order filter's price fields
const isValidPriceInput = (val) => val === "" || /^\d*\.?\d*$/.test(val);

// Round a typed price to 2 decimal places on blur, same as the Order filter's price fields
const roundPriceOnBlur = (val) => {
  if (val === "" || val === undefined || val === null) return val;
  const num = parseFloat(val);
  return isNaN(num) ? val : num.toFixed(2);
};

export default function BulkPricing() {
  const history = useHistory();
  const location = useLocation();
  const breadcrumbs = ["Pricing", "Platform Pricing", "Bulk Pricing"];

  const incomingItems = location?.state?.bulkItems ?? [];
  const [selectedItems, setSelectedItems] = useState(incomingItems);

  // Render pagination. Deliberately not tied to anything else: every other part
  // of this screen keeps working on the whole selection.
  const [bpPage, setBpPage] = useState(1);
  const bpTotalPages = Math.max(1, Math.ceil(selectedItems.length / BP_PAGE_SIZE));
  const pagedItems = useMemo(
    () => selectedItems.slice((bpPage - 1) * BP_PAGE_SIZE, bpPage * BP_PAGE_SIZE),
    [selectedItems, bpPage]
  );
  // Removing rows can leave the current page past the end.
  useEffect(() => {
    if (bpPage > bpTotalPages) setBpPage(bpTotalPages);
  }, [bpPage, bpTotalPages]);

  // What Save actually writes: one price row per SELECTED mapping. The Bulk
  // Pricing badge counts these, and so should the saving toast - it used to
  // count listings (selectedItems.length), so the badge said 3221 and the
  // toast said 2817 for one and the same selection. `platforms` is the
  // selected subset per listing, so this matches the badge at hand-off and
  // follows any rows removed here before saving.
  const selectedMappingCount = useMemo(
    () => selectedItems.reduce((n, item) => n + (item.platforms || []).length, 0),
    [selectedItems]
  );

  // Edited prices: key = `${listingId}_base` or `${listingId}_${mappingId}`
  const [editedPrices, setEditedPrices] = useState(() => {
    const init = {};
    incomingItems.forEach((item) => {
      init[`${item.id}_base`] = item.basePrice != null ? Number(item.basePrice).toFixed(2) : "";
      (item.platforms || []).forEach((p) => {
        const raw = p.override_price_raw;
        init[`${item.id}_${p.sales_listing_mapping_id}`] = raw != null ? formatSGD(raw) : "";
      });
    });
    return init;
  });

  const [priceErrors, setPriceErrors] = useState({});
  const [saveError, setSaveError] = useState(false);

  // Discard modal
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [discardSource, setDiscardSource] = useState(null);
  const [nextLocation, setNextLocation] = useState(null);
  const allowNavigationRef = useRef(false);


  // Bulk Edit panel
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkBasePrice, setBulkBasePrice] = useState("");
  // per-platform bulk config: { [platformName]: { type, value } }
  const allPlatforms = useMemo(() => {
    const seen = new Set();
    const result = [];
    selectedItems.forEach((item) =>
      (item.platforms || []).forEach((p) => {
        if (!seen.has(p.name)) { seen.add(p.name); result.push(p.name); }
      })
    );
    return result;
  }, [selectedItems]);
  const [bulkPlatformTypes, setBulkPlatformTypes] = useState({});
  const [bulkPlatformValues, setBulkPlatformValues] = useState({});
  // bulkPlatformTemplates: { [platformName]: { id, name, fees, markups, final_result_rule } }
  const [bulkPlatformTemplates, setBulkPlatformTemplates] = useState({});
  const [bulkErrors, setBulkErrors] = useState({});
  const [openBulkTypeDropdown, setOpenBulkTypeDropdown] = useState(null);
  const [openTemplateDropdown, setOpenTemplateDropdown] = useState(null);
  const bulkTypeDropdownRef = useRef({});
  const templateDropdownRef = useRef({});
  const [templateSearch, setTemplateSearch] = useState({});
  const bulkAppliedRef = useRef(false);

  // Pricing templates
  const [pricingTemplates, setPricingTemplates] = useState([]);
  useEffect(() => {
    apiHelper("sales/pricing-templates/?status=active&page_size=0", "GET")
      .then((r) => r.ok ? r.json() : { results: [] })
      .then((data) => setPricingTemplates(data.results || []))
      .catch(() => {});
  }, []);

  // Notification
  const [notification, setNotification] = useState(null);
  // Save in flight. The button showed nothing while the request ran, so on
  // live (2026-09-13) a user clicked Save ~80 times in 20 minutes against a
  // request that kept timing out — each click queued another 30s request and
  // tied up every gunicorn worker. Disable the button until the answer is in.
  const [isSaving, setIsSaving] = useState(false);

  // Unsaved changes: prices edited, a row removed, or bulk edit data entered
  const initialPricesRef = useRef(editedPrices);
  const isDirty =
    selectedItems.length !== incomingItems.length ||
    Object.keys(editedPrices).some((k) => (editedPrices[k] ?? "") !== (initialPricesRef.current[k] ?? "")) ||
    bulkBasePrice.trim() !== "" ||
    Object.values(bulkPlatformTypes).some(Boolean);

  // Browser refresh / tab close guard — shows the native "Leave site?" confirmation
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Navigation block
  useEffect(() => {
    const unblock = history.block((tx) => {
      if (allowNavigationRef.current) { allowNavigationRef.current = false; return true; }
      setDiscardSource("navigate");
      setNextLocation(tx);
      setDiscardModalOpen(true);
      return false;
    });
    return () => unblock();
  }, []);

  // Close bulk type dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        openBulkTypeDropdown !== null &&
        bulkTypeDropdownRef.current[openBulkTypeDropdown] &&
        !bulkTypeDropdownRef.current[openBulkTypeDropdown].contains(e.target)
      ) {
        setOpenBulkTypeDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openBulkTypeDropdown]);

  // Close template dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        openTemplateDropdown !== null &&
        templateDropdownRef.current[openTemplateDropdown] &&
        !templateDropdownRef.current[openTemplateDropdown].contains(e.target)
      ) {
        setOpenTemplateDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openTemplateDropdown]);


  const handleDiscard = () => {
    setDiscardSource("cancel");
    setNextLocation(null);
    setDiscardModalOpen(true);
  };

  const handleDiscardConfirm = () => {
    allowNavigationRef.current = true;
    setDiscardModalOpen(false);
    if (discardSource === "cancel") {
      history.push("/platform-pricing");
      return;
    }
    if (discardSource === "navigate" && nextLocation) {
      history.push(nextLocation.pathname, nextLocation.state || {});
      return;
    }
    history.push("/platform-pricing");
  };

  const handleDeleteRow = (itemId) => {
    setSelectedItems((prev) => prev.filter((i) => i.id !== itemId));
    setPriceErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${itemId}_`)) delete next[k]; });
      return next;
    });
    setEditedPrices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${itemId}_`)) delete next[k]; });
      return next;
    });
  };

  // Pricing Template V2.0: fees are charged on the Platform Price, so the price
  // is reverse-derived from the Base Price, then marked up by band, then
  // rounded by the template's Final Result Rule. All three steps live in
  // utils/pricingTemplate so this matches Test Calculation exactly.
  function calcTemplatePrice(baseStr, template) {
    const base = parseFloat(String(baseStr).replace(/,/g, ""));
    if (isNaN(base) || !template) return baseStr;

    // A markup ladder alone can move the price, so a template with no fee rows
    // is still worth evaluating.
    const fees = normalizeFees(template.fees || []);
    const markups = normalizeMarkups(template.markups || []);
    if (!fees.length && !markups.length) return baseStr;

    const { finalPrice } = calculatePlatformPrice(fees, markups, base, template.final_result_rule);
    return Math.max(0, finalPrice).toFixed(2);
  }

  const clearBulkType = (pname) => {
    setBulkPlatformTypes((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkPlatformValues((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkPlatformTemplates((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; return n; });
  };

  // Apply bulk edit values
  const handleBulkApply = () => {
    const errors = {};

    const bulkBase = bulkBasePrice.trim();
    // A base price of 0 counts as empty, same as the Platform Pricing table
    const isEmptyPrice = (val) => {
      const num = parseFloat(String(val ?? "").replace(/,/g, ""));
      return isNaN(num) || num <= 0;
    };

    allPlatforms.forEach((pname) => {
      const btype = bulkPlatformTypes[pname];
      if (!btype) return;

      // Base Price is only required for types whose platform price is derived
      // from it ("Use Base Price" / "Use Pricing Template"). "Bulk Input" sets
      // the platform price directly, so it doesn't need a Base Price.
      const needsBasePrice = btype === "Use Base Price" || btype === "Use Pricing Template";
      const anyBasePriceMissing = needsBasePrice && selectedItems.some((item) => {
        const hasPlatform = (item.platforms || []).some((p) => p.name === pname);
        if (!hasPlatform) return false;
        // A Woo-driven row is skipped by the bulk fill below, so its effective base
        // stays its own stored one — counting the bulk value here would pass the
        // check on a row that never receives it.
        const effectiveBase = (bulkBase !== "" && !isWooDrivenBasePrice(item))
          ? bulkBase
          : editedPrices[`${item.id}_base`];
        return isEmptyPrice(effectiveBase);
      });
      if (anyBasePriceMissing) {
        errors[pname] = "Please complete all Base Price first";  // type column
      }

      if (btype === "Use Pricing Template" && !bulkPlatformTemplates[pname]) {
        errors[`${pname}_value`] = "Please select a Pricing Template";  // value column
      } else if (btype === "Bulk Input") {
        const val = (bulkPlatformValues[pname] || "").trim();
        if (!val) {
          errors[`${pname}_value`] = "Value is required";  // value column
        } else if (isNaN(Number(val.replace(/,/g, "")))) {
          errors[`${pname}_value`] = "Invalid Price";  // value column
        }
      }
    });

    setBulkErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const updates = {};
    selectedItems.forEach((item) => {
      // ERP-5169: Base Price on a Woo-mapped listing is owned by WooCommerce, so
      // the bulk value passes it by — its own figure stays, and the Platform Price
      // types below ("Use Base Price" / "Use Pricing Template") keep deriving from
      // that figure rather than from a value that would be reverted on save.
      if (bulkBasePrice.trim() && !isWooDrivenBasePrice(item)) {
        updates[`${item.id}_base`] = bulkBasePrice.trim();
      }
      (item.platforms || []).forEach((p) => {
        const btype = bulkPlatformTypes[p.name];
        if (!btype) return;
        const key = `${item.id}_${p.sales_listing_mapping_id}`;
        const baseVal = updates[`${item.id}_base`] ?? editedPrices[`${item.id}_base`] ?? "";
        if (btype === "Bulk Input") {
          updates[key] = bulkPlatformValues[p.name] || "";
        } else if (btype === "Use Base Price") {
          updates[key] = baseVal;
        } else if (btype === "Use Pricing Template") {
          const tmpl = bulkPlatformTemplates[p.name];
          updates[key] = baseVal ? calcTemplatePrice(baseVal, tmpl) : "";
        }
      });
    });
    // The bulk tool can auto-fill a Lazada row above its Normal Price (e.g. a
    // flat Bulk Input value, or a template-derived price) — flag those rows
    // the same way a manual edit or Save would.
    const bulkPriceErrors = {};
    selectedItems.forEach((item) => {
      (item.platforms || []).forEach((p) => {
        const key = `${item.id}_${p.sales_listing_mapping_id}`;
        if (!(key in updates)) return;
        const numVal = parseFloat(String(updates[key]).replace(/,/g, ""));
        if (isLazadaPlatform(p.name) && p.normalPriceRaw != null && !isNaN(numVal) && numVal > p.normalPriceRaw) {
          bulkPriceErrors[key] = `Cannot exceed SGD ${formatMoney(p.normalPriceRaw)}`;
        }
      });
    });

    setEditedPrices((prev) => ({ ...prev, ...updates }));
    setPriceErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach((k) => { delete next[k]; });
      return { ...next, ...bulkPriceErrors };
    });
    bulkAppliedRef.current = true;
  };

  const handleSaveClick = async () => {
    if (isSaving) return;
    const errors = {};
    let hasError = false;
    selectedItems.forEach((item) => {
      // Validate platform prices
      (item.platforms || []).forEach((p) => {
        const key = `${item.id}_${p.sales_listing_mapping_id}`;
        const val = editedPrices[key];
        if (!val || val.trim() === "") {
          errors[key] = "Platform Price is required";
          hasError = true;
        } else if (isNaN(Number(String(val).replace(/,/g, "")))) {
          errors[key] = "Invalid Price";
          hasError = true;
        } else if (
          isLazadaPlatform(p.name) &&
          p.normalPriceRaw != null &&
          Number(String(val).replace(/,/g, "")) > p.normalPriceRaw
        ) {
          errors[key] = `Cannot exceed SGD ${formatMoney(p.normalPriceRaw)}`;
          hasError = true;
        }
      });
    });
    setPriceErrors(errors);
    if (hasError) {
      // The rows are paginated, so the red field can be on a page the user is
      // not looking at. Before pagination every row was on one long page and
      // the error was at least somewhere on screen; with 20 per page a bare
      // "Error Saving" on a page where nothing is red is a dead end - live,
      // 2026-09-18, 2,820 listings, one Lazada row over its Normal Price on a
      // page the user never reached. Jump to the first page that has an error
      // and say how many there are.
      const errorCount = Object.keys(errors).length;
      const firstBadIdx = selectedItems.findIndex((item) =>
        (`${item.id}_base` in errors) ||
        (item.platforms || []).some((p) => `${item.id}_${p.sales_listing_mapping_id}` in errors)
      );
      if (firstBadIdx >= 0) setBpPage(Math.floor(firstBadIdx / BP_PAGE_SIZE) + 1);
      setSaveError(true);
      setNotification({
        type: "error",
        message: errorCount === 1
          ? "1 price needs attention — showing its page"
          : `${errorCount} prices need attention — showing the first`,
      });
      return;
    }
    setSaveError(false);
    setNotification(null);

    const listingsPayload = selectedItems.map((item) => ({
      listing_id: item.id,
      prices: (item.platforms || []).map((p) => {
        const baseKey = `${item.id}_base`;
        const priceKey = `${item.id}_${p.sales_listing_mapping_id}`;
        const baseVal = editedPrices[baseKey] || "";
        const overrideVal = editedPrices[priceKey] || "";
        const parsedBase = parseFloat(String(baseVal).replace(/[^\d.]/g, ""));
        const parsedOverride = parseFloat(String(overrideVal).replace(/,/g, ""));
        const row = {
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          sales_platform_id: p.id,
          override_price: !isNaN(parsedOverride) ? parsedOverride : 0,
        };
        // Three outcomes, and the middle one is the whole point:
        //   a number  -> send it
        //   emptied   -> send an explicit null, i.e. CLEAR the stored base price
        //   never had one -> omit the key, leaving the stored figure alone
        //
        // It used to re-send `item.basePrice` when the field was empty, which made
        // clearing a Base Price impossible: the user emptied the box, saved, and the
        // old figure came straight back because the screen had quietly put it back in
        // the payload. The backend distinguishes these cases by whether the key is
        // PRESENT (`if "base_price" in price`), so an explicit null is a real clear
        // while an omitted key is "don't touch it" - the same contract the quick edit
        // on Platform Pricing has always used.
        //
        // ERP-5169: omit base_price entirely on a Woo-driven listing. The backend
        // would overwrite it from the Woo price anyway (sync_listing_base_price_from_woo),
        // but sending it makes the write look like a base price change and is one
        // more way for a stale figure to land in the price history. That is also why
        // clearing works on exactly the listings it should: with no Woo price to
        // follow - no Woo mapping, a Woo price of 0, or the "No Connected Model"
        // placeholder - the sync is a no-op and the NULL stands.
        if (!isWooDrivenBasePrice(item)) {
          if (!isNaN(parsedBase)) row.base_price = parsedBase;
          else if (item.basePrice != null) row.base_price = null;
        }
        return row;
      }),
    }));

    setIsSaving(true);
    try {
      const res = await apiHelper("sales/pricing/bulk-pricing/", "POST", {
        reason: "Price update",
        listings: listingsPayload,
      });
      if (res.ok) {
        // ERP-5170: 200 means every price is saved in ERP, so the toast is the
        // green one either way. `counts` carries the Success / Failed / Pending
        // breakdown Platform Pricing renders under the title — plain data, because
        // router state is structured-cloned and a React element would not survive
        // the trip. Pending is a Lazada campaign lock or a deferred push: both go
        // out by themselves, neither is a failure.
        const body = await res.json().catch(() => ({}));
        allowNavigationRef.current = true;
        history.push("/platform-pricing", {
          notification: {
            type: "success",
            message: "Pricing Saved Successfully",
            counts: body.counts || null,
          },
        });
      } else {
        const err = await res.json().catch(() => ({}));
        console.error("Save failed", err);
        setNotification({ type: "error", message: "Failed to save pricing" });
      }
    } catch (e) {
      console.error(e);
      setNotification({ type: "error", message: "Network error" });
    } finally {
      setIsSaving(false);
    }
  };

  const isEmpty = selectedItems.length === 0;

  // Amber "Campaign" pill (Figma) next to a Lazada platform whose price is
  // locked by a running campaign; mirrors the Delist pill on Platform Listing.
  // Delist pill — same look as Platform Listing / Platform Pricing.
  const delistBadgeStyle = {
    display: "flex",
    padding: "2px 5px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 99,
    border: "1px solid #B02A37",
    background: "#F8D7DA",
    color: "#B02A37",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: 8,
    fontWeight: 600,
    lineHeight: "normal",
    flexShrink: 0,
  };

  const campaignBadgeStyle = {
    display: "inline-flex",
    padding: "2px 5px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 99,
    border: "1px solid #CC9A06",
    background: "#FFF3CD",
    color: "#CC9A06",
    fontFamily: "Montserrat",
    fontSize: 8,
    fontWeight: 600,
    lineHeight: "normal",
    flexShrink: 0,
  };

  const inputWrapStyle = (hasError) => ({
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${hasError ? "#DC3545" : "#6C757D"}`,
    borderRadius: 8,
    height: 40,
    overflow: "hidden",
    background: "#fff",
    width: "100%",
    maxWidth: 220,
  });

  const sgdStyle = (hasError) => ({
    padding: "0 10px",
    fontSize: 14,
    color: hasError ? "#DC3545" : "#6C757D",
    borderRight: `1px solid ${hasError ? "#DC3545" : "#6C757D"}`,
    height: "100%",
    display: "flex",
    alignItems: "center",
    background: "#F8F9FA",
    whiteSpace: "nowrap",
  });

  const inputStyle = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    padding: "0 10px",
    fontSize: 14,
    height: "100%",
    flex: 1,
    minWidth: 0,
    color: "#212529",
    fontFamily: "Montserrat",
  };

  return (
    <div className="company-body main-company-body-inner new-look bulk-pricing-root">
      {/* "Breathing light" — ERP-5169. The same dot the reference design puts beside
          the Base Price pencil on Platform Pricing: 10px solid #CC9A06 (the Campaign
          amber) inside a soft halo about 7px deep. Only the halo animates, so nothing
          reflows. Here it leads the "Base Price not set" caption under the input. */}
      <style>{`
        @keyframes bp-breathe {
          0%, 100% { box-shadow: 0 0 0 2px rgba(204,154,6,0.22); }
          50%      { box-shadow: 0 0 0 7px rgba(204,154,6,0.10); }
        }
        .bp-breathe-dot {
          display: inline-block; width: 10px; height: 10px; border-radius: 99px;
          background: #CC9A06; flex-shrink: 0;
          animation: bp-breathe 2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .bp-breathe-dot { animation: none; box-shadow: 0 0 0 4px rgba(204,154,6,0.16); }
        }
      `}</style>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          <span
            key={i}
            className={`breadcrumb-item${crumb === "Platform Pricing" ? " clickable" : ""}`}
            style={{ cursor: crumb === "Platform Pricing" ? "pointer" : "default" }}
            onClick={() => crumb === "Platform Pricing" && history.push("/platform-pricing")}
          >
            {crumb}{i < breadcrumbs.length - 1 ? "  " : ""}
          </span>
        ))}
      </div>

      {/* Header */}
      <div className="edit-organisation-info">
        <div className="edit-organisation-left">
          <span style={{ color: "#212529", fontFamily: "Montserrat", fontSize: 20, fontWeight: 700 }}>
            Bulk Pricing
          </span>
        </div>
        <div className="discard-save-organisation-right d-flex align-items-center justify-content-end gap-2">
          {/* One slot at the top of the page: the save's progress, then its
              result. Both are pinned to the same fixed 86px offset — mounted
              here only because this is where the Save button lives; neither is
              laid out by this row. Mutually exclusive so they cannot stack. */}
          {isSaving ? (
            <LoadingNotification
              message={`Saving ${selectedMappingCount} Pricing Record${selectedMappingCount === 1 ? "" : "s"}`}
              total={selectedMappingCount}
            />
          ) : saveError && notification ? (
            <AlertNotification
              type={notification.type}
              message={notification.message}
              onClose={() => { setNotification(null); setSaveError(false); }}
            />
          ) : null}
          <button
            type="button"
            onClick={handleDiscard}
            style={{
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              borderRadius: 8,
              border: "1px solid #212529",
              background: "#fff",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#212529",
              cursor: "pointer",
            }}
          >
            Discard
          </button>
          <button
            type="button"
            disabled={isEmpty || isSaving}
            onClick={!isEmpty && !isSaving ? handleSaveClick : undefined}
            style={isEmpty ? {
              borderRadius: 8,
              border: "1px solid #CED4DA",
              background: "#E9ECEF",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              cursor: "not-allowed",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#6C757D",
            } : {
              borderRadius: 8,
              border: "1px solid #212529",
              background: "#212529",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              cursor: isSaving ? "progress" : "pointer",
              opacity: isSaving ? 0.7 : 1,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#fff",
            }}
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <DiscardConfirmationModal
        isOpen={discardModalOpen}
        onCancel={() => { setDiscardModalOpen(false); setNextLocation(null); setDiscardSource(null); }}
        onConfirm={handleDiscardConfirm}
        entityName="Bulk Pricing"
      />


      <div className="employee-tabs-container">
        <div className="tab-content" id="bulk-pricing-v2-content">
          <div className="info-section">
            <div className="info-card new-info-overflow-auto main-company-body-inner" style={{ borderRadius: 12, boxShadow: "0px 1px 2px rgba(0,0,0,0.1)" }}>
              <div className="info-content d-block entities-tab">

                <>
                  {/* Bulk Edit Button + Panel */}
                  {!isEmpty && <div style={{ background: "#F8F9FA", borderRadius: 8, padding: 10, marginBottom: 20 }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (bulkEditOpen && !bulkAppliedRef.current) {
                          setBulkBasePrice("");
                          setBulkPlatformTypes({});
                          setBulkPlatformValues({});
                          setBulkPlatformTemplates({});
                          setBulkErrors({});
                          setTemplateSearch({});
                        }
                        if (bulkEditOpen) bulkAppliedRef.current = false;
                        setBulkEditOpen((o) => !o);
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "10px", borderRadius: 8, border: "none",
                        backgroundColor: "#212529", color: "#fff",
                        fontFamily: "Montserrat", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      Bulk Edit
                      {bulkEditOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {bulkEditOpen && (
                        <div style={{ marginTop: 10, borderRadius: 10, background: "#F8F9FA", padding: "16px" }}>
                          {/* Bulk Base Price */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 11, flexWrap: "nowrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap" }}>
                                Bulk Edit Base Price
                              </span>
                              <span style={{ fontSize: 10, color: "#6C757D", fontFamily: "Montserrat", fontWeight: 400, whiteSpace: "nowrap" }}>
                                (Applied to all Base Price values below, except listings mapped to WooCommerce)
                              </span>
                            </div>
                            <div style={{ ...inputWrapStyle(false), maxWidth: 525 }}>
                              <div style={sgdStyle(false)}>SGD</div>
                              <input
                                type="text"
                                placeholder="0.00"
                                value={bulkBasePrice}
                                onChange={(e) => {
                                  if (!isValidPriceInput(e.target.value)) return;
                                  setBulkBasePrice(e.target.value);
                                  // Bulk base price fills every row, so base-price errors are no longer certain
                                  setBulkErrors((prev) => {
                                    const n = { ...prev };
                                    Object.keys(n).forEach((k) => { if (!k.endsWith("_value")) delete n[k]; });
                                    return n;
                                  });
                                }}
                                onBlur={(e) => setBulkBasePrice(roundPriceOnBlur(e.target.value))}
                                style={inputStyle}
                              />
                            </div>
                          </div>

                          {/* Bulk Platform Price */}
                          {!isEmpty && <div style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "nowrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap" }}>
                                Bulk Edit Platform Price
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 400, color: "#6C757D", fontFamily: "Montserrat", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                (This is a one-time bulk pricing tool. Price correlations between "Base Price" and "Platform Price" won't be saved. Only updating "Base Price" later will not auto-refresh "Platform Price".)
                              </span>
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: "#fff" }}>
                              <colgroup>
                                <col style={{ width: "25%" }} />
                                <col style={{ width: "37.5%" }} />
                                <col style={{ width: "37.5%" }} />
                              </colgroup>
                              <thead>
                                <tr>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Platform</th>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Bulk Pricing Type</th>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allPlatforms.map((pname) => {
                                  const selectedType = bulkPlatformTypes[pname] || "";
                                  const isDropOpen = openBulkTypeDropdown === pname;
                                  const isTemplateDropOpen = openTemplateDropdown === pname;
                                  const typeError = bulkErrors[pname];
                                  const valueError = bulkErrors[`${pname}_value`];
                                  const hasTypeError = !!typeError;
                                  const hasValueError = !!valueError;
                                  return (
                                    <tr key={pname}>
                                      <td style={{ padding: "10px", fontSize: 12, color: "#212529", fontFamily: "Montserrat", borderBottom: "1px solid #DEE2E6", height: 60 }}>{pname}</td>

                                      {/* Bulk Pricing Type dropdown */}
                                      <td style={{ padding: "10px", borderBottom: "1px solid #DEE2E6", height: 60 }}>
                                        <div
                                          ref={(el) => { bulkTypeDropdownRef.current[pname] = el; }}
                                          style={{ position: "relative", display: "block", width: "100%" }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => setOpenBulkTypeDropdown(isDropOpen ? null : pname)}
                                            style={{
                                              width: "100%", height: 40, display: "flex", alignItems: "center", justifyContent: "space-between",
                                              padding: "0 10px",
                                              border: `1px solid ${hasTypeError ? "#DC3545" : "#6C757D"}`,
                                              borderRadius: 8, background: "#fff",
                                              fontSize: 14, color: selectedType ? "#212529" : "#6C757D",
                                              fontFamily: "Montserrat", cursor: "pointer",
                                            }}
                                          >
                                            <span>{selectedType ? BULK_TYPE_DISPLAY[selectedType] : "Select..."}</span>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                              {selectedType && (
                                                <span
                                                  onClick={(e) => { e.stopPropagation(); clearBulkType(pname); }}
                                                  style={{ display: "flex", alignItems: "center", color: "#6C757D", cursor: "pointer" }}
                                                >
                                                  <X size={13} />
                                                </span>
                                              )}
                                              <ChevronDown size={14} color="#6C757D" />
                                            </div>
                                          </button>
                                          {hasTypeError && (
                                            <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{typeError}</div>
                                          )}
                                          {isDropOpen && (
                                            <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, background: "#fff", border: "1px solid #DEE2E6", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", width: "100%", marginTop: 4 }}>
                                              {BULK_TYPES.map((t) => (
                                                <div
                                                  key={t}
                                                  onClick={() => {
                                                    setBulkPlatformTypes((prev) => ({ ...prev, [pname]: t }));
                                                    setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                    setOpenBulkTypeDropdown(null);
                                                  }}
                                                  style={{ padding: "9px 14px", fontSize: 14, fontWeight: 400, fontFamily: "Montserrat", color: "#212529", cursor: "pointer", background: "#fff" }}
                                                  onMouseEnter={(e) => e.currentTarget.style.background = "#FFFEF0"}
                                                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                                                >
                                                  {t}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </td>

                                      {/* Value column */}
                                      <td style={{ padding: "10px", borderBottom: "1px solid #DEE2E6", height: 60 }}>
                                        {selectedType === "Bulk Input" ? (
                                          <div>
                                            <div style={{ ...inputWrapStyle(hasValueError), maxWidth: "none", width: "100%" }}>
                                              <div style={sgdStyle(hasValueError)}>SGD</div>
                                              <input
                                                type="text"
                                                placeholder="0.00"
                                                value={bulkPlatformValues[pname] || ""}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  if (!isValidPriceInput(v)) return;
                                                  setBulkPlatformValues((prev) => ({ ...prev, [pname]: v }));
                                                  if (bulkErrors[pname] || bulkErrors[`${pname}_value`]) setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                }}
                                                onBlur={(e) => {
                                                  const rounded = roundPriceOnBlur(e.target.value);
                                                  setBulkPlatformValues((prev) => ({ ...prev, [pname]: rounded }));
                                                }}
                                                style={{ ...inputStyle, color: hasValueError ? "#DC3545" : "#6C757D" }}
                                              />
                                            </div>
                                            {hasValueError && (
                                              <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{valueError}</div>
                                            )}
                                          </div>
                                        ) : selectedType === "Use Pricing Template" ? (
                                          <div
                                            ref={(el) => { templateDropdownRef.current[pname] = el; }}
                                            style={{ position: "relative", display: "block", width: "100%" }}
                                          >
                                            <div style={{ display: "flex", alignItems: "center", height: 40, border: `1px solid ${hasValueError ? "#DC3545" : "#6C757D"}`, borderRadius: 8, background: "#fff", padding: "0 10px", gap: 4 }}>
                                              <input
                                                type="text"
                                                placeholder="Search and Select..."
                                                value={isTemplateDropOpen ? (templateSearch[pname] || "") : (bulkPlatformTemplates[pname]?.name || "")}
                                                onFocus={() => {
                                                  setTemplateSearch((prev) => ({ ...prev, [pname]: "" }));
                                                  setOpenTemplateDropdown(pname);
                                                }}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  setTemplateSearch((prev) => ({ ...prev, [pname]: v }));
                                                  if (!isTemplateDropOpen) setOpenTemplateDropdown(pname);
                                                }}
                                                style={{ border: "none", outline: "none", flex: 1, fontSize: 14, fontFamily: "Montserrat", color: "#212529", background: "transparent", minWidth: 0 }}
                                              />
                                              {bulkPlatformTemplates[pname] && !isTemplateDropOpen && (
                                                <span
                                                  onMouseDown={(e) => { e.preventDefault(); setBulkPlatformTemplates((prev) => { const n = { ...prev }; delete n[pname]; return n; }); setOpenTemplateDropdown(null); }}
                                                  style={{ display: "flex", alignItems: "center", color: "#6C757D", cursor: "pointer", flexShrink: 0 }}
                                                >
                                                  <X size={13} />
                                                </span>
                                              )}
                                              <ChevronDown size={14} color="#6C757D" style={{ flexShrink: 0 }} />
                                            </div>
                                            {isTemplateDropOpen && (
                                              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, background: "#fff", border: "1px solid #DEE2E6", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", width: "100%", marginTop: 4, maxHeight: 220, overflowY: "auto" }}>
                                                {(() => {
                                                  const q = (templateSearch[pname] || "").toLowerCase();
                                                  const filtered = pricingTemplates.filter((t) => t.template_name.toLowerCase().includes(q));
                                                  if (!filtered.length) return (
                                                    <div style={{ padding: "10px 14px", fontSize: 12, color: "#ADB5BD", fontFamily: "Montserrat" }}>No templates found</div>
                                                  );
                                                  return filtered.map((t) => (
                                                    <div
                                                      key={t.pricing_template_id}
                                                      onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setBulkPlatformTemplates((prev) => ({ ...prev, [pname]: { id: t.pricing_template_id, name: t.template_name, fees: t.fees || [], markups: t.markups || [], final_result_rule: t.final_result_rule } }));
                                                        setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                        setOpenTemplateDropdown(null);
                                                        setTemplateSearch((prev) => ({ ...prev, [pname]: "" }));
                                                      }}
                                                      style={{ padding: "9px 14px", fontSize: 13, fontFamily: "Montserrat", color: "#212529", cursor: "pointer", background: "#fff" }}
                                                      onMouseEnter={(e) => e.currentTarget.style.background = "#FFFEF0"}
                                                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                                                    >
                                                      {t.template_name}
                                                    </div>
                                                  ));
                                                })()}
                                              </div>
                                            )}
                                          {hasValueError && selectedType === "Use Pricing Template" && (
                                            <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{valueError}</div>
                                          )}
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: 12, color: "#212529", fontFamily: "Montserrat" }}>-</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>}

                          {/* Panel footer */}
                          {(() => {
                            const hasBulkData = bulkBasePrice.trim() !== "" || Object.values(bulkPlatformTypes).some((t) => !!t);
                            return (
                              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                <button
                                  type="button"
                                  onClick={() => { setBulkEditOpen(false); setBulkBasePrice(""); setBulkPlatformTypes({}); setBulkPlatformValues({}); setBulkPlatformTemplates({}); setBulkErrors({}); setTemplateSearch({}); bulkAppliedRef.current = false; }}
                                  style={{ height: 34, minWidth: 100, padding: "0 10px", borderRadius: 8, border: "none", background: "#fff", fontSize: 12, fontFamily: "Montserrat", fontWeight: 600, cursor: "pointer", boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)", color: "#212529" }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={hasBulkData ? handleBulkApply : undefined}
                                  style={{
                                    borderRadius: 8,
                                    border: hasBulkData ? "none" : "1px solid #CED4DA",
                                    background: hasBulkData ? "#212529" : "#E9ECEF",
                                    color: hasBulkData ? "#fff" : "#6C757D",
                                    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
                                    display: "flex",
                                    height: 34,
                                    minWidth: 100,
                                    padding: "0 10px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 5,
                                    fontSize: 12,
                                    fontFamily: "Montserrat",
                                    fontWeight: 600,
                                    cursor: hasBulkData ? "pointer" : "not-allowed",
                                  }}
                                >
                                  Apply
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>}

                  {isEmpty ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 20px 30px" }}>
                      <img src={emptyBoxImg} alt="No listings" style={{ width: 120, height: 120, objectFit: "contain", marginBottom: 16 }} />
                      <h2 style={{ fontSize: 20, fontWeight: 600, color: "#212529", fontFamily: "Montserrat", marginBottom: 8 }}>No Listings Selected</h2>
                      <p style={{ fontSize: 14, color: "#6C757D", fontFamily: "Montserrat", lineHeight: "18px" }}>Back to select Listings and initiate bulk pricing</p>
                    </div>
                  ) : (
                    <div className="table-wrapper" style={{ overflowX: "auto", border: "none" }}>
                      {/* table-layout:fixed + an explicit colgroup, because minWidth alone
                          does NOT hold a width: with the default auto layout the browser hands
                          the spare space back out, which rendered Base Price at 260 and Cost
                          Price at 198 however small their minWidths were. Widths are the
                          reference design's grid: 50 / 220 / 90 / 160 / 540 / 190 — except
                          Cost Price, which stays at 180: the design's 90px only works because
                          its cells WRAP, and a range value ("SGD 260.00 -> SGD 728.00") is
                          ~155px on one line. whiteSpace:normal there too, as a safety net
                          against the global nowrap+ellipsis clipping a longer value. */}
                      <table className="entities-table tab-table w-100" style={{ tableLayout: "fixed" }}>
                        <colgroup>
                          <col style={{ width: 50 }} />
                          <col style={{ width: 220 }} />
                          <col style={{ width: 180 }} />
                          <col style={{ width: 160 }} />
                          <col />{/* Platform Listing: no width — in a fixed layout the one
                                      unsized column takes whatever is left, so the five fixed
                                      columns (50+220+180+160+190 = 800) always fit and Platform
                                      Price stays on screen without horizontal scrolling. The
                                      reference's 540 would total 1340 and push it out of view. */}
                          <col style={{ width: 190 }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th />
                            <th>Stock</th>
                            <th style={{ whiteSpace: "normal" }}>Cost Price</th>
                            <th>Base Price</th>
                            <th>Platform Listing</th>
                            <th>Platform Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedItems.map((item, idx) => {
                            const platforms = item.platforms || [];
                            const baseKey = `${item.id}_base`;
                            const basePriceVal = editedPrices[baseKey] ?? "";
                            // ERP-5169: a listing with a priced WooCommerce mapping has its
                            // Base Price re-derived from that Woo price by the backend after
                            // every save, so typing one here would be undone silently. Show
                            // the figure, greyed and read-only. A listing with no Woo price
                            // and no Base Price yet gets the breathing prompt instead.
                            const wooDriver = wooBasePriceDriver(item);
                            const wooDriven = wooDriver != null;
                            const basePriceNeedsAttention = !wooDriven && isBasePriceEmpty(basePriceVal);
                            // The field mirrors the WooCommerce price *as it is being typed* on
                            // this same row, which is what the reference design shows: edit
                            // WooCommerce to 30 and Base Price reads 30 before you even save.
                            // Falling back to the stored base when that field is empty or 0 is
                            // not cosmetic — woo_base_price_for_listing requires > 0, so a
                            // cleared Woo price leaves the base price exactly where it was.
                            const wooLiveVal = wooDriven ? (editedPrices[`${item.id}_${wooDriver.sales_listing_mapping_id}`] ?? "") : "";
                            const shownBaseVal = wooDriven && !isBasePriceEmpty(wooLiveVal) ? wooLiveVal : basePriceVal;
                            // All rows white — the grey/white striping was dropped on review.
                            // Each listing already sits in its own grey block inside the Platform
                            // Listing cell, so striping the row as well gave two competing greys.
                            return (
                              <tr key={item.id} style={{ background: "#FFFFFF" }}>
                                {/* Delete — 10px of horizontal padding, not the tab-table
                                    default 20px. At 20px this 50px column had only 10px of
                                    content box for an 18px icon, so the cell overflowed and the
                                    stylesheet's text-overflow:ellipsis painted a stray "..."
                                    next to the bin. */}
                                <td style={{ verticalAlign: "middle", textAlign: "center", paddingLeft: 10, paddingRight: 10 }}>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRow(item.id)}
                                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                      <path d="M2.25 4.5H15.75M14.25 4.5V15C14.25 15.75 13.5 16.5 12.75 16.5H5.25C4.5 16.5 3.75 15.75 3.75 15V4.5M6 4.5V3C6 2.25 6.75 1.5 7.5 1.5H10.5C11.25 1.5 12 2.25 12 3V4.5M7.5 8.25V12.75M10.5 8.25V12.75" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                </td>

                                {/* Stock */}
                                <td style={{ verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <img src={item.avatar || noImage} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 400, color: "#212529", fontFamily: "Montserrat" }}>{item.name}</div>
                                      <div style={{ fontSize: 10, color: "#6C757D", marginTop: 2, fontFamily: "Montserrat" }}>{item.variant}{item.model_number ? ` | ${item.model_number}` : ""}</div>
                                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                                        <StockTags condition={item.condition} conditionType={item.grading} setType={item.setType} className="" style={{ border: "1px solid #FFEB00", backgroundColor: "#FFEB00", color: "#212529", fontSize: 8, fontWeight: 600, borderRadius: 99, padding: "2px 5px", fontFamily: "Montserrat" }} />
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Cost Price */}
                                <td style={{ verticalAlign: "middle", fontSize: 12, color: "#212529", whiteSpace: "normal" }}>
                                  {item.costPriceData
                                    ? item.costPriceData.is_uniform
                                      ? `SGD ${Number(item.costPriceData.min).toFixed(2)}`
                                      : `SGD ${Number(item.costPriceData.min).toFixed(2)} → SGD ${Number(item.costPriceData.max).toFixed(2)}`
                                    : <span style={{ color: "#ADB5BD" }}>-</span>}
                                </td>

                                {/* Base Price — 10px of horizontal padding, not the
                                    tab-table default 20px. At 20px the 160px column left the
                                    input wrapper only 120px (design: 140), so the number field
                                    was 67px instead of 85 and "100.00" ran to the edge with
                                    ~5px to spare. */}
                                {/* overflow:visible — company.scss gives every `td > div` in an
                                    .entities-table overflow:hidden for text truncation, which
                                    here cut the breathing dot's 7px halo off at the cell's left
                                    edge and flattened it. Nothing in this cell needs truncating. */}
                                <td style={{ verticalAlign: "middle", paddingLeft: 10, paddingRight: 10 }}>
                                  <div style={{ minWidth: 0, overflow: "visible" }}>
                                    <div
                                      title={wooDriven ? WOO_BASE_PRICE_LOCKED_TITLE : undefined}
                                      style={{
                                        ...inputWrapStyle(false),
                                        ...(wooDriven ? { background: "#F8F9FA", borderColor: "#CED4DA" } : null),
                                      }}
                                    >
                                      <div style={{ ...sgdStyle(false), ...(wooDriven ? { borderRightColor: "#CED4DA" } : null) }}>SGD</div>
                                      <input
                                        type="text"
                                        placeholder="0.00"
                                        value={shownBaseVal}
                                        disabled={wooDriven}
                                        onChange={(e) => { const v = e.target.value; if (!isValidPriceInput(v)) return; setEditedPrices((prev) => ({ ...prev, [baseKey]: v })); }}
                                        onBlur={(e) => {
                                          const rounded = roundPriceOnBlur(e.target.value);
                                          setEditedPrices((prev) => ({ ...prev, [baseKey]: rounded }));
                                        }}
                                        style={wooDriven ? { ...inputStyle, color: "#6C757D", background: "#F8F9FA", cursor: "not-allowed" } : inputStyle}
                                      />
                                    </div>
                                    {/* One caption line under the input, always one of two
                                        (Jess, 16 Sep): the breathing dot with "Base Price not
                                        set" while the field is empty and nothing owns it, or
                                        "Base Price will match WooCommerce Price" while
                                        WooCommerce does. The dot used to sit BESIDE the input;
                                        the reference design puts it here, leading the caption.
                                        7px vertical margin on the dot reserves room for its
                                        halo so the line is 24px tall and the halo never clips. */}
                                    {wooDriven && (
                                      <div style={{ fontSize: 10, lineHeight: "13px", color: "#6C757D", fontFamily: "Montserrat", marginTop: 4, whiteSpace: "normal" }}>
                                        Base Price will match WooCommerce Price
                                      </div>
                                    )}
                                    {basePriceNeedsAttention && (
                                      <div
                                        title={BASE_PRICE_BREATHE_TITLE}
                                        style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 10, lineHeight: "13px", color: "#6C757D", fontFamily: "Montserrat", whiteSpace: "normal" }}
                                      >
                                        <span className="bp-breathe-dot" style={{ margin: "7px 0" }} />
                                        <span>Base Price not set</span>
                                      </div>
                                    )}
                                  </div>
                                </td>

                                {/* Platform Listing + Platform Price — ONE cell spanning both
                                    columns, which is what the reference design does
                                    (grid-column: span 2). Each listing is a flex row holding its
                                    detail block and its own price input with align-items:stretch,
                                    so the input always sits beside the listing it belongs to even
                                    though the blocks differ in height (the product name wraps to
                                    one or two lines). Two separate cells stacking independently
                                    drift apart as soon as one name wraps — which is the whole
                                    point of this change: with several TikTok listings on one
                                    stock you could not tell which price you were editing. */}
                                {/* paddingRight:0 — .entities-table.tab-table td carries 20px of
                                    horizontal padding, which pushed the price input 20px left of the
                                    "Platform Price" header column. Dropping the right padding makes
                                    the 220px price side land exactly under its own header. */}
                                <td colSpan={2} style={{ verticalAlign: "middle", paddingRight: 0 }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {platforms.map((p) => {
                                      const key = `${item.id}_${p.sales_listing_mapping_id}`;
                                      const val = editedPrices[key] ?? "";
                                      const hasErr = !!priceErrors[key];
                                      const showNormal = isLazadaPlatform(p.name) && p.normalPriceRaw != null;
                                      return (
                                        <div key={p.sales_listing_mapping_id} style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
                                          {/* Listing detail — platform, product, SKU, notes */}
                                          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3, padding: "8px 10px", background: "#F8F9FA", borderRadius: 8, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                                              <span style={{ width: 6, height: 6, borderRadius: 99, flexShrink: 0, background: p.dotColor || "#146C43" }} />
                                              <span style={{ fontSize: 12, fontWeight: 600, color: p.textColor || "#212529", fontFamily: "Montserrat", flexShrink: 0 }}>{p.name}</span>
                                              {p.isDelisted && (
                                                <span style={delistBadgeStyle}>Delist</span>
                                              )}
                                              {p.isCampaign && (
                                                <span
                                                  title="Under Lazada campaign — the platform price is frozen. Your edit is saved in ERP and pushed to Lazada once the campaign ends (retried daily 1:00pm SGT)."
                                                  style={campaignBadgeStyle}
                                                >
                                                  Campaign
                                                </span>
                                              )}
                                            </div>
                                            <span style={{ fontSize: 11, lineHeight: 1.4, color: "#212529", fontFamily: "Montserrat" }}>
                                              {p.platformProductName || <span style={{ color: "#ADB5BD" }}>-</span>}
                                            </span>
                                            <span style={{ fontSize: 10, lineHeight: "12px", color: "#6C757D", fontFamily: "Montserrat" }}>
                                              {p.platformSkuName || "-"}
                                            </span>
                                            <span style={{ fontSize: 10, lineHeight: "12px", color: "#6C757D", fontFamily: "Montserrat" }}>
                                              Notes: {(p.notes || "").trim() || "-"}
                                            </span>
                                          </div>

                                          {/* Price input — centred against its own block */}
                                          <div style={{ width: 190, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                            <div style={inputWrapStyle(hasErr)}>
                                              <div style={sgdStyle(hasErr)}>SGD</div>
                                              <input
                                                type="text"
                                                placeholder="0.00"
                                                value={val}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  if (!isValidPriceInput(v)) return;
                                                  setEditedPrices((prev) => ({ ...prev, [key]: v }));
                                                  if (priceErrors[key]) setPriceErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
                                                }}
                                                onBlur={(e) => {
                                                  const rounded = roundPriceOnBlur(e.target.value);
                                                  setEditedPrices((prev) => ({ ...prev, [key]: rounded }));

                                                  const numVal = parseFloat(String(rounded).replace(/,/g, ""));
                                                  if (
                                                    isLazadaPlatform(p.name) &&
                                                    p.normalPriceRaw != null &&
                                                    !isNaN(numVal) &&
                                                    numVal > p.normalPriceRaw
                                                  ) {
                                                    setPriceErrors((prev) => ({
                                                      ...prev,
                                                      [key]: `Cannot exceed SGD ${formatMoney(p.normalPriceRaw)}`,
                                                    }));
                                                  }
                                                }}
                                                style={{ ...inputStyle, color: hasErr ? "#DC3545" : "#6C757D" }}
                                              />
                                            </div>
                                            {hasErr && (
                                              <div style={{ color: "#DC3545", fontSize: 11, marginTop: 2, fontFamily: "Montserrat" }}>
                                                {priceErrors[key]}
                                              </div>
                                            )}
                                            {showNormal && (
                                              <div style={{ color: "#666666", fontSize: 11, marginTop: 2, fontFamily: "Montserrat" }}>
                                                Normal Price: {formatMoney(p.normalPriceRaw)}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Render pagination. The range counts sales listings, the
                      same unit Platform Pricing pages by. Edits made on other
                      pages are held in `editedPrices` and still saved. */}
                  {!isEmpty && selectedItems.length > BP_PAGE_SIZE && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 4px 0" }}>
                      <span style={{ fontSize: 12, color: "#6C757D" }}>
                        {`${(bpPage - 1) * BP_PAGE_SIZE + 1} - ${Math.min(bpPage * BP_PAGE_SIZE, selectedItems.length)} of ${selectedItems.length}`}
                      </span>
                      <Pagination currentPage={bpPage} totalPages={bpTotalPages} onPageChange={setBpPage} />
                    </div>
                  )}
                </>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
